import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useApp, ROLE_LABELS } from '../../context/AppContext';
import {
  LayoutDashboard, Users, MapPin, Store, Calendar,
  BarChart3, Route, ChevronLeft, ChevronRight, GitBranch,
  ShieldCheck, Download, Shield, X, LogOut, Navigation, ClipboardCheck, FileCode2, UserPlus, Receipt
} from 'lucide-react';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', featureKey: 'dashboard' },
  { to: '/team', icon: Users, label: 'Team Management', featureKey: 'team' },
  { to: '/org-tree', icon: GitBranch, label: 'Org Tree', featureKey: 'org-tree' },
  { to: '/visits', icon: Calendar, label: 'Client Visits', featureKey: 'visits' },
  { to: '/places', icon: MapPin, label: 'Business Places', featureKey: 'places' },
  { to: '/approvals', icon: ShieldCheck, label: 'Approvals', featureKey: 'approvals' },
  { to: '/sales-points', icon: Store, label: 'Sales Points', featureKey: 'sales-points' },
  { to: '/leads', icon: UserPlus, label: 'Leads', featureKey: 'leads' },
  { to: '/performance', icon: BarChart3, label: 'Performance', featureKey: 'performance' },
  { to: '/planner', icon: Route, label: 'Visit Planner', featureKey: 'planner' },
  { to: '/travel-plans', icon: Navigation, label: 'Travel Plans', featureKey: 'travel-plans' },
  { to: '/travel-approvals', icon: ClipboardCheck, label: 'Travel Approvals', featureKey: 'travel-approvals' },
  { to: '/expense-claims', icon: Receipt, label: 'Expense Claims', featureKey: 'expense-claims' },
  { to: '/import', icon: Download, label: 'Data Import', featureKey: 'import' },
  { to: '/admin', icon: Shield, label: 'Admin Panel', featureKey: 'admin' },
  { to: '/docs', icon: FileCode2, label: 'API Docs', featureKey: 'admin' },
];

export const Sidebar = ({ collapsed, onToggle, isMobile, mobileOpen, onMobileClose }) => {
  const { currentUser, setCurrentUser, roleFeatures, setSelectedBusiness } = useApp();
  const location = useLocation();

  const visibleItems = navItems.filter(item => roleFeatures.includes(item.featureKey));

  const handleNavClick = () => {
    if (isMobile && onMobileClose) onMobileClose();
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-border/40 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.jpg" alt="SalesTracker" className="w-9 h-9 rounded-lg flex-shrink-0 object-cover" />
          {(!collapsed || isMobile) && (
            <div className="overflow-hidden">
              <h1 className="text-base font-bold text-slate-900 truncate">SalesTracker</h1>
              <p className="text-xs text-slate-400 truncate">Performance Hub</p>
            </div>
          )}
        </div>
        {isMobile && (
          <button onClick={onMobileClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        )}
      </div>

      {/* Nav Links */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {visibleItems.map(item => {
          const isActive = location.pathname === item.to ||
            (item.to !== '/' && location.pathname.startsWith(item.to));
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`sidebar-link ${isActive ? 'active' : ''}`}
              title={collapsed && !isMobile ? item.label : undefined}
              onClick={handleNavClick}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
              {(!collapsed || isMobile) && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* User info */}
      {currentUser && (
        <div className="p-4 border-t border-border/40">
          <div className={`flex items-center gap-3 ${collapsed && !isMobile ? 'justify-center' : ''}`}>
            <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
              {currentUser.name?.charAt(0) || 'U'}
            </div>
            {(!collapsed || isMobile) && (
              <div className="overflow-hidden flex-1">
                <p className="text-sm font-semibold text-slate-900 truncate">{currentUser.name}</p>
                <p className="text-xs text-slate-400 truncate">{ROLE_LABELS[currentUser.role] || currentUser.role}</p>
              </div>
            )}
            <button
              data-testid="logout-btn"
              onClick={() => { setCurrentUser(null); setSelectedBusiness(null); }}
              title="Logout"
              className={`flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors ${collapsed && !isMobile ? 'mt-2' : ''}`}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );

  // Mobile overlay sidebar
  if (isMobile) {
    return (
      <>
        {mobileOpen && (
          <div data-testid="mobile-backdrop" className="fixed inset-0 bg-black/40 z-40 transition-opacity" onClick={onMobileClose} />
        )}
        <aside
          data-testid="sidebar"
          className={`fixed left-0 top-0 h-full w-72 bg-white z-50 flex flex-col shadow-xl transition-transform duration-300 ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {sidebarContent}
        </aside>
      </>
    );
  }

  // Desktop sidebar
  return (
    <aside
      data-testid="sidebar"
      className={`fixed left-0 top-0 h-full border-r border-border bg-white z-50 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'} flex flex-col`}
    >
      {sidebarContent}
      <button
        data-testid="sidebar-toggle"
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-white border border-border shadow-sm flex items-center justify-center hover:bg-slate-50 transition-colors"
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-500" /> : <ChevronLeft className="w-3.5 h-3.5 text-slate-500" />}
      </button>
    </aside>
  );
};
